import { Cause, Deferred, Effect, Exit, Match, Option, Redacted, Ref, Result } from "effect";
import { workerstationService } from "../../effect/service.ts";
import { failureReasons } from "../../effect/settlement.ts";
import { privateDependencyFailure } from "../../errors/private-cause.ts";
import { prepareSpecialistAttemptModel, finishSpecialistAttemptModel, failedSpecialistAttemptResult,
  type SpecialistProvider, type SpecialistProviderRequest, type SpecialistRunnerInput, type SpecialistRunOptions,
  type SpecialistFailurePolicy, type SpecialistFailureClassification } from "./model.ts";

export const SpecialistAttemptError = /* @__PURE__ */ (() => {
  class SpecialistAttemptError extends privateDependencyFailure<SpecialistAttemptError>()("SpecialistAttemptError", "specialist attempt") {}
  return SpecialistAttemptError;
})();
export type SpecialistAttemptError = InstanceType<typeof SpecialistAttemptError>;
export const specialistAttemptError = (cause: unknown) => new SpecialistAttemptError({ cause: Redacted.make(cause) });
export class SpecialistAttemptIo extends workerstationService<SpecialistAttemptIo, {
  readonly isCancelled: (options: SpecialistRunOptions) => Effect.Effect<boolean, SpecialistAttemptError>;
  readonly watch: (options: SpecialistRunOptions, notify: () => void) => Effect.Effect<{ readonly current: Effect.Effect<boolean, SpecialistAttemptError>; readonly release: Effect.Effect<void, SpecialistAttemptError> }, SpecialistAttemptError>;
  readonly review: (provider: SpecialistProvider, request: SpecialistProviderRequest) => Effect.Effect<Result.Result<unknown, unknown>, SpecialistAttemptError>;
  readonly classify: (failure: unknown, policy: SpecialistFailurePolicy) => Effect.Effect<SpecialistFailureClassification, SpecialistAttemptError>;
}>()("development/SpecialistAttemptIo") {}

export const prepareSpecialistAttempt = Effect.fn("Specialist.prepareAttempt")(function* (provider: SpecialistProvider, input: SpecialistRunnerInput, options: SpecialistRunOptions) {
  const prepared = yield* Effect.try({ try: () => prepareSpecialistAttemptModel(provider, input, options), catch: specialistAttemptError });
  const cancelled = yield* (yield* SpecialistAttemptIo).isCancelled(options);
  return { ...prepared, cancelled, provider, options };
});
export const executeSpecialistAttempt = Effect.fn("Specialist.executeAttempt")(function* (prepared: Effect.Success<ReturnType<typeof prepareSpecialistAttempt>>) {
  const io = yield* SpecialistAttemptIo;
  const { request, identity, policy, provider, options, timeoutMs } = prepared;
  if (prepared.cancelled) return failedSpecialistAttemptResult(identity, { kind: "cancelled", action: "cancel" });
  const stopped = yield* Deferred.make<void>();
  const providerReceipt = yield* Ref.make<Option.Option<Exit.Exit<Result.Result<unknown, unknown>, SpecialistAttemptError>>>(Option.none());
  const outcome = yield* Effect.uninterruptibleMask(restore => Effect.gen(function* () {
    const subscription = yield* io.watch(options, () => { Deferred.doneUnsafe(stopped, Effect.void); });
    const selected = yield* Effect.exit(restore(Effect.gen(function* () {
      if (yield* subscription.current) return { _tag: "Cancelled" as const };
      return yield* Effect.raceFirst(
      io.review(provider, request).pipe(
        Effect.onExit(exit => Ref.set(providerReceipt, Option.some(exit))), Effect.exit,
        Effect.map(exit => ({ _tag: "Reply" as const, exit }))),
      Effect.raceFirst(Effect.sleep(timeoutMs).pipe(Effect.as({ _tag: "Timeout" as const })),
        Deferred.await(stopped).pipe(Effect.as({ _tag: "Cancelled" as const })))
    );
    })));
    const released = yield* Effect.exit(subscription.release);
    const settledProvider = yield* Ref.get(providerReceipt);
    // The native finally refusal precedes the selected reply; retain both receipts.
    const reasons = [
      ...failureReasons(released, selected),
      ...(Option.isSome(settledProvider) && Exit.isFailure(settledProvider.value)
        ? settledProvider.value.cause.reasons.filter(reason => !Cause.isInterruptReason(reason)
          || (Exit.isSuccess(selected) && selected.value._tag === "Reply")) : [])
    ];
    if (reasons.length) return yield* Effect.failCause(Cause.fromReasons(reasons));
    return yield* selected;
  }));
  return yield* Match.value(outcome).pipe(
    Match.tag("Cancelled", () => Effect.succeed(
      failedSpecialistAttemptResult(identity, { kind: "cancelled", action: "cancel" })
    )),
    Match.tag("Timeout", () => Effect.succeed(failedSpecialistAttemptResult(identity, {
      kind: "timeout",
      action: policy.providerAttempt < policy.maxProviderAttempts ? "retry" : policy.hasFailover ? "failover" : "permanent"
    }))),
    Match.tag("Reply", ({ exit }) => Effect.gen(function* () {
      const reply = yield* exit;
      return yield* Match.value(reply).pipe(
        Match.tag("Failure", ({ failure }) => io.classify(failure, policy).pipe(
          Effect.map(classification => failedSpecialistAttemptResult(identity, classification))
        )),
        Match.tag("Success", ({ success }) => Effect.try({
          try: () => finishSpecialistAttemptModel(success, request, identity, policy),
          catch: specialistAttemptError
        })),
        Match.exhaustive
      );
    })),
    Match.exhaustive
  );
});
export const runSpecialistAttempt = Effect.fn("Specialist.runAttempt")(function* (provider: SpecialistProvider, input: SpecialistRunnerInput, options: SpecialistRunOptions) {
  return yield* executeSpecialistAttempt(yield* prepareSpecialistAttempt(provider, input, options));
});
